/**
 * Feature flags ระดับร้าน — Admin เปิด/ปิดต่อร้าน; แยกจาก RBAC
 * ให้สอดคล้องกับ Merchant_POS `shop-feature-flags.ts`
 * @see Merchant_Pos/docs/IMPLEMENT_SHOP_FEATURE_FLAGS.md
 */

export const SHOP_FEATURE_FLAG_KEYS = [
  "inventory",
  "warehouse_locations",
  "warehouse_layout",
  "shifts",
  "provision_sellable",
  "claims",
  "procurement",
  "multi_branch",
  "settlement",
  "marketplace_sync",
  "marketplace_auto_publish",
  "promotions",
  "workshop",
] as const;

export type ShopFeatureFlagId = (typeof SHOP_FEATURE_FLAG_KEYS)[number];

export type ShopFeatureFlags = Record<ShopFeatureFlagId, boolean>;

export const DEFAULT_SHOP_FEATURE_FLAGS: ShopFeatureFlags = {
  inventory: true,
  warehouse_locations: false,
  warehouse_layout: false,
  shifts: true,
  provision_sellable: true,
  claims: true,
  procurement: true,
  multi_branch: true,
  settlement: true,
  marketplace_sync: true,
  marketplace_auto_publish: false,
  promotions: true,
  workshop: false,
};

function isBool(v: unknown): v is boolean {
  return typeof v === "boolean";
}

function normalizeFeatureFlagsRaw(raw: unknown): unknown {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  return raw;
}

export function mergeShopFeatureFlags(raw: unknown): ShopFeatureFlags {
  const out = { ...DEFAULT_SHOP_FEATURE_FLAGS };
  const normalized = normalizeFeatureFlagsRaw(raw);
  if (
    normalized == null ||
    typeof normalized !== "object" ||
    Array.isArray(normalized)
  ) {
    return out;
  }
  const o = normalized as Record<string, unknown>;
  // Backward compat: legacy key `product_requests` → `provision_sellable`
  const legacyProvision = o.product_requests;
  if (!("provision_sellable" in o) && isBool(legacyProvision)) {
    out.provision_sellable = legacyProvision;
  }
  for (const k of SHOP_FEATURE_FLAG_KEYS) {
    if (k in o && isBool(o[k])) {
      out[k] = o[k];
    }
  }
  return out;
}

export function isShopFeatureEnabled(
  flags: ShopFeatureFlags | undefined | null,
  key: ShopFeatureFlagId,
): boolean {
  if (flags == null) return true;
  return flags[key] !== false;
}
